<template>
  <section class="t-wrapper pt-4 pb-4" style="min-height: 100%">
    <div class="container">
      <div class="d-flex flex-column align-items-center">
        <h3 class="mm-font text-center mb-1">ဟောစာတမ်းများ</h3>
        <div class="pane-line"></div>
        <h5 class="text-center mt-2 mb-4 mm-font">
          သင် ၃လအတွင်း မေးထားခဲ့ဖူးသော ဟောစာတမ်းများ
        </h5>

        <!-- Loading -->
        <div v-if="isLoading" class="loading-section">
          <div class="offer-icon" ref="lottieContainer"></div>
          <p class="mm-font loading-text">ဟောစာတမ်းများ ဖတ်နေပါသည်…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="error-section">
          <span class="error-icon">⚠️</span>
          <p class="mm-font error-text">ဟောစာတမ်းများ ဖတ်ရန် မအောင်မြင်ပါ</p>
          <span class="back-btn" @click="retry">ပြန်ကြိုးစားမည်</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!orders.length" class="empty-section">
          <span class="empty-icon">🗂️</span>
          <p class="mm-font empty-text">ဟောစာတမ်း မရှိသေးပါ</p>
        </div>

        <!-- Order list -->
        <div v-else class="w-100">
          <div
            v-for="(order, index) in orders"
            :key="order.order_id || index"
            class="order-card"
            :style="{ animationDelay: index * 60 + 'ms' }"
          >
            <div class="order-card-top">
              <img
                v-if="categoryIcon(order.category_name)"
                :src="categoryIcon(order.category_name)"
                class="order-icon-img"
                alt=""
              />
              <span
                v-else
                class="order-icon"
                :class="categoryTint(order.category_name)"
              >🎁</span>
              <div class="order-main">
                <p class="order-name mm-font">
                  {{ getServiceName(order.category_name) }}
                </p>
                <div class="order-meta">
                  <span class="order-meta-item">
                    <span class="meta-dot price-dot"></span>
                    {{ order.amount }} MMK
                  </span>
                  <span class="order-meta-item">
                    <span class="meta-dot date-dot"></span>
                    {{ moment(order.createdAt).format("YYYY-MM-DD HH:mm") }}
                  </span>
                </div>
              </div>
            </div>

            <div class="order-actions">
              <span
                v-if="isInstantAnswer(order.category_name)"
                class="instant-badge"
              >
                ⚡ အဖြေထွက်ပြီးသားဖြစ်ပါသည်
              </span>
              <template v-else>
                <span
                  class="action-btn action-btn-outline"
                  @click="goQuestion(order)"
                  >မေးခွန်းကြည့်ရန်</span
                >
                <span
                  class="action-btn action-btn-primary"
                  @click="goAnswer(order)"
                  >အဖြေကြည့်ရန်</span
                >
              </template>
            </div>
          </div>

          <div class="text-center mt-4">
            <span class="back-btn" @click="back">&#8592; back</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { HTTP } from "@core/lib/http-common";
import { mapGetters } from "vuex";
import lottie from "lottie-web";
import animationData from "@/assets/Loading Dots Blue.json";

const INSTANT_ANSWER_CATEGORIES = ["compatability_love", "tarot_shuffle"];

// Last successfully fetched orders, kept across visits so returning to this
// page can show the previous list immediately while silently refreshing.
const orderCache = { orders: [], userId: null, openID: null };

const CATEGORY_ICONS = {
  tarot_shuffle: require("@/images/orders/tarot_shuffle.jpg"),
  compatability_love: require("@/images/orders/compatability_love.jpg"),
  tarot: require("@/images/orders/tarot.jpg"),
  newyearbaydin: require("@/images/orders/newyearbaydin.jpg"),
  clairvoyance: require("@/images/orders/clairvoyance.jpg"),
  chiromancy: require("@/images/orders/chiromancy.jpg"),
  child: require("@/images/orders/child.jpg"),
  love: require("@/images/orders/love.jpg"),
  phone: require("@/images/orders/phone.jpg"),
};

const CATEGORY_META = {
  tarot_shuffle: { tint: "tint-3" },
  compatability_love: { tint: "tint-1" },
  tarot: { tint: "tint-2" },
  newyearbaydin: { tint: "tint-6" },
  clairvoyance: { tint: "tint-2" },
  chiromancy: { tint: "tint-4" },
  child: { tint: "tint-8" },
  love: { tint: "tint-1" },
  phone: { tint: "tint-5" },
  specialoffer: { tint: "tint-6" },
};

export default {
  name: "OrderByCategory",
  data() {
    return {
      isLoading: orderCache.orders.length === 0,
      loadError: false,
      lottieLoaded: false,
      categories: {
        tarot_shuffle: "တစ်နေ့တာ တားရော့ကံကြမ္မာ",
        compatability_love: "ဖူးစာဖက်",
        tarot: "တားရော့",
        newyearbaydin: "တစ်နှစ်စာ အဟော",
        clairvoyance: "အကြားအမြင်",
        chiromancy: "လက္ခဏာ",
        child: "အမည်ပေး(ရင်သွေးအမည်)",
        love: "အချစ်ဇာတာဟောစာတမ်း",
        phone: "ဗေဒင်ဆရာမှဖုန်းပြန်လည်ခေါ်ဆိုပေးမည်",
        specialoffer: "စပယ်ရှယ်အော်ဖာ",
      },
      orders: [...orderCache.orders],
      userId: orderCache.userId,
    };
  },
  computed: {
    ...mapGetters({
      getUserToken: "userStore/getUserToken",
      getResponse: "userStore/getResponse",
    }),
  },
  watch: {
    getResponse: {
      immediate: true,
      deep: true,
      async handler(newVal) {
        const openID = newVal?.customer_info?.openID;

        if (!openID) {
          // Login not complete yet — show the spinner only when we have
          // nothing to display.
          this.isLoading = this.orders.length === 0;
          return;
        }

        await this.fetchOrders(openID);
      },
    },
    isLoading(val) {
      if (val) {
        this.$nextTick(() => this.maybeStartLottie());
      } else {
        this.lottieLoaded = false;
      }
    },
  },
  methods: {
    getServiceName(category_name) {
      return this.categories[category_name] || category_name;
    },
    isInstantAnswer(category_name) {
      return INSTANT_ANSWER_CATEGORIES.includes(category_name);
    },
    categoryIcon(category_name) {
      return CATEGORY_ICONS[category_name] || null;
    },
    categoryTint(category_name) {
      return (CATEGORY_META[category_name] || {}).tint || "tint-5";
    },
    goQuestion(order) {
      this.$router.push({
        name: "ordersDetails",
        params: {
          orderId: order.order_id,
          serviceName: this.getServiceName(order.category_name),
          price: order.amount,
          date: order.createdAt,
          categoryId: order.category_id,
          userId: this.userId,
        },
      });
    },
    goAnswer(order) {
      this.$router.push({
        name: "answer",
        params: {
          orderId: order.order_id,
          serviceName: this.getServiceName(order.category_name),
          price: order.amount,
          date: order.createdAt,
          categoryId: order.category_id,
          userId: this.userId,
        },
      });
    },
    back() {
      history.back();
    },
    async fetchOrders(openID) {
      this.loadError = false;
      // Remember the openID so retry() works even if the login payload is
      // no longer in the store.
      orderCache.openID = openID;
      // If we already have orders to show, refresh silently (no spinner).
      this.isLoading = this.orders.length === 0;
      try {
        HTTP.defaults.headers.Authorization = `Bearer ${this.getUserToken}`;
        const response = await HTTP.get(
          `subscription/orders_new/openID=${openID}`,
        );
        this.orders = response.data.order || [];
        this.userId = response.data.user_id;
        orderCache.orders = this.orders;
        orderCache.userId = this.userId;
        this.isLoading = false;
      } catch (error) {
        console.error(error);
        this.isLoading = false;
        // Only surface the error state when there is nothing to fall back on.
        if (this.orders.length === 0) {
          this.loadError = true;
        }
      }
    },
    retry() {
      const openID =
        this.getResponse?.customer_info?.openID || orderCache.openID;
      if (openID) {
        this.fetchOrders(openID);
      }
    },
    maybeStartLottie() {
      if (this.lottieLoaded || !this.$refs.lottieContainer) return;
      this.lottieLoaded = true;
      lottie.loadAnimation({
        container: this.$refs.lottieContainer,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: animationData,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid meet",
        },
      });
    },
  },
  mounted() {
    this.maybeStartLottie();
  },
};
</script>

<style scoped>
.t-wrapper {
  color: #1f2937;
}

.mm-font {
  font-family: "Padauk", sans-serif;
  line-height: 175%;
}

.t-wrapper h3 {
  font-size: 18px;
}

.t-wrapper h5 {
  font-size: 15px;
  color: #4b5563;
}

div.pane-line {
  width: 60px;
  height: 5px;
  border-radius: 30px;
  background: linear-gradient(90deg, #ff7010, #ffb066) !important;
  box-shadow: 0 2px 10px rgba(255, 112, 16, 0.35);
}

/* Loading */
.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 300px;
  padding: 24px 0;
}

.loading-text {
  margin: 0;
  font-size: 15px;
  color: #4b5563;
  text-align: center;
}

.offer-icon {
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.offer-icon svg {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

/* Empty */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 260px;
  padding: 24px 0;
}

.empty-icon {
  font-size: 44px;
  opacity: 0.7;
}

.empty-text {
  margin: 0;
  font-size: 15px;
  color: #6b7280;
  text-align: center;
}

/* Error */
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 260px;
  padding: 24px 0;
}

.error-icon {
  font-size: 44px;
  opacity: 0.7;
}

.error-text {
  margin: 0;
  font-size: 15px;
  color: #6b7280;
  text-align: center;
}

/* Order card */
.order-card {
  background: #ffffff;
  border: 1.5px solid #ffd8b8;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(255, 112, 16, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease, background 0.15s ease;
  animation: card-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.order-card:hover {
  border-color: #ff7010;
  box-shadow: 0 8px 20px rgba(255, 112, 16, 0.18);
  transform: translateY(-3px);
  background: #fff8f2;
}

.order-card-top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.order-icon-img {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: contain;
  border-radius: 50%;
}

.order-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
}

.order-main {
  min-width: 0;
}

.order-name {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
}

.order-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.order-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #6b7280;
}

.meta-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.price-dot {
  background: #ff7010;
}

.date-dot {
  background: #c4ccd6;
}

/* Actions */
.order-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #ffe0c7;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 30px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.action-btn:active {
  opacity: 0.7;
}

.action-btn-primary {
  background: #ff7010;
  color: #ffffff;
  border: 1px solid #ff7010;
}

.action-btn-primary:hover {
  background: #ff8a3d;
  border-color: #ff8a3d;
  box-shadow: 0 4px 12px rgba(255, 112, 16, 0.25);
}

.action-btn-outline {
  background: #ffffff;
  color: #ff7010;
  border: 1px solid #ff7010;
}

.action-btn-outline:hover {
  background: #fff3e6;
}

/* Instant answer badge */
.instant-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 30px;
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
}

/* Back */
.back-btn {
  font-weight: bold;
  font-size: 16px;
  background-color: #ff7010;
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
  display: inline-block;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background-color: #ff8a3d;
  box-shadow: 0 4px 12px rgba(255, 112, 16, 0.25);
}

.back-btn:active {
  opacity: 0.7;
}

/* Tints (match TarotShuffle) */
.tint-1 {
  background: linear-gradient(145deg, #ffe3ec, #ffc9dc);
}
.tint-2 {
  background: linear-gradient(145deg, #f1e6ff, #ddc7ff);
}
.tint-3 {
  background: linear-gradient(145deg, #fff3c9, #ffe08a);
}
.tint-4 {
  background: linear-gradient(145deg, #e3f7e5, #b9ecc0);
}
.tint-5 {
  background: linear-gradient(145deg, #e3edff, #c2d9ff);
}
.tint-6 {
  background: linear-gradient(145deg, #e0f6ff, #b8e8ff);
}
.tint-7 {
  background: linear-gradient(145deg, #fff0e0, #ffd7ad);
}
.tint-8 {
  background: linear-gradient(145deg, #eae6ff, #cfc4ff);
}

@keyframes card-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media screen and (max-width: 480px) {
  .order-name {
    font-size: 14px;
  }

  .order-meta-item {
    font-size: 12px;
  }

  .action-btn {
    flex: 1;
    min-width: 0;
  }
}
</style>
